import { useEnvironment } from '@publicisgroupe/p-os-components/build/contexts/EnvironmentContext';
import axios from 'axios';
import { useEffect } from 'react';
import { getHostByModule } from 'utils/helpers';
import { ModuleEnum } from '../../utils/enums';

export interface MicroModuleProps {
  moduleName: ModuleEnum;
  history?: any;
}

type RenderApp = (containerId: string, host: string, history?: any) => void;
type UnmountApp = (app: string) => void;

export const MicroModule = (props: MicroModuleProps) => {
  const { moduleName, history } = props;
  const { settings, isLoaded } = useEnvironment();
  const containerId = `${moduleName}-container`;

  useEffect(() => {
    if (!isLoaded) return;

    let fileAssets: string[] = [];
    const host = getHostByModule(settings, moduleName);
    const scriptId = `micro-frontend-script-${moduleName}`;

    const renderMicroFrontend = (hostname: string) => {
      const renderApp: RenderApp = (window as { [key: string]: any })[`render${moduleName}`];

      if (typeof renderApp == 'function') {
        renderApp(containerId, hostname, history);
      }
    };

    const prepareScript = (entryPoint: string) => {
      const script = document.createElement('script');
      script.id = 'microfrontend-' + moduleName + '-' + entryPoint;
      script.crossOrigin = '';
      script.src = `${host}/${entryPoint}`;
      script.onload = () => {
        try {
          renderMicroFrontend(host);
        } catch (e) {}
      };
      document.head.appendChild(script);
    };

    if (document.getElementById(scriptId)) {
      renderMicroFrontend(host);
      return;
    }

    const cancelTokenSource = axios.CancelToken.source();
    const controller = new AbortController();
    const signal = controller.signal;

    const http = axios.create({
      baseURL: host,
      cancelToken: cancelTokenSource.token,
    });

    http
      .get('/asset-manifest.json', { signal })
      .then(({ data: manifest }) => {
        fileAssets = [];

        for (const key in manifest.files) {
          if (Object.prototype.hasOwnProperty.call(manifest.files, key)) {
            const fileName = manifest.files[key];
            fileAssets.push(fileName);
          }
        }

        const entrypoints = manifest.entrypoints;
        entrypoints.forEach((entryPoint: string) => {
          prepareScript(entryPoint);
        });
      })
      .catch((err) => {
        if (err.name === 'AbortError') {
          console.log('fetch aborted');
        } else {
          console.error('webframe micromodule request error', err);
        }
      });

    return () => {
      cancelTokenSource.cancel('cancel on unmount');
      controller.abort();

      const unmountApp: UnmountApp = (window as { [key: string]: any })[`unmount${moduleName}`];
      if (unmountApp && document.getElementById(containerId)) {
        unmountApp(containerId);
      }

      cleanupAssets(fileAssets);
    };
  }, [containerId, history, isLoaded, moduleName, settings]);

  return <></>;
};

function cleanupAssets(fileAssets: string[]) {
  for (const asset of fileAssets) {
    let file = asset;
    if (file.startsWith('./')) {
      file = file.substring(1, file.length);
    }
    const styles = document.head.querySelectorAll(`*[href$="${file}"]`);
    const scripts = document.head.querySelectorAll(`*[src$="${file}"]`);
    removeObsoleteAssets(styles);
    removeObsoleteAssets(scripts);
  }
}

function removeObsoleteAssets(existingAssets: any) {
  for (const element of existingAssets) {
    document.head.removeChild(element);
  }
}
